import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  MailOutline as MailOutlineIcon,
  OpenInNew as OpenInNewIcon,
  PictureInPicture as PictureInPictureIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Send as SendIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import MiniVideoPlayer from './MiniVideoPlayer';
import {
  createSupportTicketApiCall,
  SUPPORT_TICKET_MAX_BODY_LENGTH,
} from '../../../services/api/support';
import { buildSupportMailtoHref, SUPPORT_EMAIL } from '../../../utils/constants/support';

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  title: string;
  description: string;
  enableSupportSection?: boolean;
}

/** Shared glass styling for the dialog surface (frosted panel). */
const glassPaperSx = {
  maxHeight: '90vh',
  borderRadius: 3,
  overflow: 'hidden',
  background: 'linear-gradient(155deg, rgba(255,255,255,0.88) 0%, rgba(248,252,255,0.72) 48%, rgba(240,248,255,0.78) 100%)',
  backdropFilter: 'blur(22px) saturate(165%)',
  WebkitBackdropFilter: 'blur(22px) saturate(165%)',
  border: '1px solid rgba(255, 255, 255, 0.75)',
  boxShadow: `
    0 12px 40px rgba(25, 55, 95, 0.14),
    0 4px 12px rgba(25, 118, 210, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.95)
  `,
};

const glassBackdropSx = {
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(18, 42, 72, 0.38)',
};

const glassInsetSx = {
  borderRadius: 2,
  background: 'rgba(255, 255, 255, 0.42)',
  border: '1px solid rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7)',
};

const glassFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(8px)',
    borderRadius: 2,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.72)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.82)',
    },
  },
};

/** Tabbed body: height follows content (no fixed min-tall box); scroll only if needed on small viewports. */
const tabbedContentSx = {
  pt: 2,
  pb: 1.5,
  px: 3,
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: 'calc(100vh - 180px)',
  boxSizing: 'border-box',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(240,248,255,0.28) 100%)',
};

const HelpModal = ({
  open,
  onClose,
  videoUrl,
  title,
  description,
  enableSupportSection = false,
}: HelpModalProps) => {
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);
  const [tab, setTab] = useState(0);
  const user = useSelector((state: any) => state.user.user);

  const [ticketBody, setTicketBody] = useState('');
  const [contactEmailInput, setContactEmailInput] = useState('');
  const [forceContactEmail, setForceContactEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const profileEmail = user?.ContactEmail?.trim() ?? '';
  const showContactEmailField = enableSupportSection && (!profileEmail || forceContactEmail);

  useEffect(() => {
    if (!open) {
      setTab(0);
      setTicketBody('');
      setContactEmailInput('');
      setForceContactEmail(false);
      setSending(false);
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const handleOpenVideo = () => {
    window.open(videoUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenMiniPlayer = () => {
    setShowMiniPlayer(true);
    onClose();
  };

  const handleCloseMiniPlayer = () => {
    setShowMiniPlayer(false);
  };

  const getVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const videoId = getVideoId(videoUrl);

  const trimmedBody = ticketBody.trim();
  const emailTrim = contactEmailInput.trim();
  const bodyTooLong = ticketBody.length > SUPPORT_TICKET_MAX_BODY_LENGTH;
  const canSend =
    trimmedBody.length > 0 &&
    !bodyTooLong &&
    !sending &&
    (!showContactEmailField || emailTrim.length > 0);

  const handleSendTicket = async () => {
    if (!canSend) return;
    setError(null);
    setSuccess(false);
    setSending(true);
    try {
      const payload: { body: string; contactEmail?: string } = { body: trimmedBody };
      if (showContactEmailField && emailTrim) {
        payload.contactEmail = emailTrim;
      }
      await createSupportTicketApiCall(payload);
      setSuccess(true);
      setTicketBody('');
      setContactEmailInput('');
      setForceContactEmail(false);
    } catch (e: unknown) {
      const message =
        e instanceof AxiosError && e.response?.data?.message
          ? String(e.response.data.message)
          : 'Could not send your message. Please try again.';
      setError(message);
      if (e instanceof AxiosError && e.response?.status === 400) {
        const lower = message.toLowerCase();
        if (lower.includes('email') || lower.includes('contact')) {
          setForceContactEmail(true);
          if (profileEmail && !contactEmailInput.trim()) {
            setContactEmailInput(profileEmail);
          }
        }
      }
    } finally {
      setSending(false);
    }
  };

  const dialogTitle = enableSupportSection ? 'Help' : title;

  const tabbedTutorial = enableSupportSection;

  const tutorialPanel = (
    <>
      <Typography
        variant={tabbedTutorial ? 'body2' : 'body1'}
        color="text.secondary"
        sx={{
          mb: tabbedTutorial ? 1.25 : 3,
          textAlign: 'center',
          lineHeight: tabbedTutorial ? 1.5 : 1.65,
          ...(tabbedTutorial && { fontSize: '0.8125rem' }),
        }}
      >
        {description}
      </Typography>

      <Box
        sx={{
          mb: tabbedTutorial ? 1 : 2,
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
        }}
      >
        {videoId && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 2.5,
              overflow: 'hidden',
              backgroundColor: 'rgba(0,0,0,0.35)',
              boxShadow: '0 8px 24px rgba(25, 55, 95, 0.2)',
              border: '1px solid rgba(255,255,255,0.35)',
            }}
          >
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: tabbedTutorial ? 1.5 : 2,
          width: '100%',
          justifyContent: 'center',
          flexWrap: 'wrap',
          pt: tabbedTutorial ? 0.5 : 1,
        }}
      >
        <Button
          onClick={handleOpenVideo}
          variant="contained"
          size={tabbedTutorial ? 'small' : 'medium'}
          startIcon={<OpenInNewIcon />}
          sx={{
            minWidth: tabbedTutorial ? 158 : 180,
            py: tabbedTutorial ? 0.75 : 1.5,
            fontSize: tabbedTutorial ? '0.8125rem' : '15px',
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
          }}
        >
          Watch in New Tab
        </Button>
        <Button
          onClick={handleOpenMiniPlayer}
          variant="outlined"
          size={tabbedTutorial ? 'small' : 'medium'}
          startIcon={<PictureInPictureIcon />}
          sx={{
            minWidth: tabbedTutorial ? 140 : 160,
            py: tabbedTutorial ? 0.75 : 1.5,
            fontSize: tabbedTutorial ? '0.8125rem' : '15px',
            fontWeight: 600,
            borderRadius: 2,
            borderColor: 'rgba(25, 118, 210, 0.55)',
            color: 'primary.main',
            backgroundColor: 'rgba(255, 255, 255, 0.45)',
            backdropFilter: 'blur(8px)',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
            },
          }}
        >
          Mini Player
        </Button>
      </Box>
    </>
  );

  const supportPanel = (
    <Box sx={{ ...glassInsetSx, py: 2.5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.65 }}>
        Write to us at{' '}
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, userSelect: 'all' }}>
          {SUPPORT_EMAIL}
        </Box>
        .
      </Typography>
      <Button
        component="a"
        href={buildSupportMailtoHref()}
        variant="outlined"
        size="small"
        startIcon={<MailOutlineIcon />}
        title="Opens your mail app with this address and a suggested subject (you can edit before sending)"
        sx={{
          mb: 1.5,
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontWeight: 600,
          borderColor: 'rgba(25, 118, 210, 0.45)',
          color: 'primary.main',
          backgroundColor: 'rgba(255, 255, 255, 0.35)',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(255, 255, 255, 0.55)',
          },
        }}
      >
        Open your mail app
      </Button>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
        Prefer to stay here? Describe your request below - we&apos;ll get back to you.
      </Typography>
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, backgroundColor: 'rgba(46, 125, 50, 0.12)', backdropFilter: 'blur(6px)' }}
          onClose={() => setSuccess(false)}
        >
          Your message has been sent.
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)', backdropFilter: 'blur(6px)' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {showContactEmailField && (
        <TextField
          fullWidth
          size="small"
          type="email"
          label="Reply-to email"
          value={contactEmailInput}
          onChange={e => setContactEmailInput(e.target.value)}
          sx={{ mb: 2, ...glassFieldSx }}
          required
          disabled={sending}
        />
      )}
      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Your request"
        placeholder="Describe what you need help with…"
        value={ticketBody}
        onChange={e => setTicketBody(e.target.value)}
        disabled={sending}
        error={bodyTooLong}
        helperText={
          bodyTooLong
            ? `Maximum ${SUPPORT_TICKET_MAX_BODY_LENGTH.toLocaleString()} characters`
            : `${ticketBody.length.toLocaleString()} / ${SUPPORT_TICKET_MAX_BODY_LENGTH.toLocaleString()}`
        }
        sx={{ mb: 2, ...glassFieldSx }}
      />
      <Button
        variant="contained"
        startIcon={<SendIcon />}
        disabled={!canSend}
        onClick={handleSendTicket}
        sx={{
          py: 1.25,
          px: 3,
          borderRadius: 2,
          fontWeight: 600,
          boxShadow: '0 4px 16px rgba(25, 118, 210, 0.35)',
        }}
      >
        {sending ? 'Sending…' : 'Send'}
      </Button>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        BackdropProps={{
          sx: glassBackdropSx,
        }}
        PaperProps={{
          sx: {
            ...glassPaperSx,
            ...(enableSupportSection && {
              display: 'flex',
              flexDirection: 'column',
              // `maxWidth="sm"` (~600px): wide enough for text + 16:9 video without feeling cramped
              maxHeight: '90vh',
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            pb: enableSupportSection ? 0.75 : 2,
            pt: enableSupportSection ? 1.5 : 2.5,
            px: 3,
            borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.08) 100%)',
          }}
        >
          <Typography
            variant={enableSupportSection ? 'h6' : 'h5'}
            component="div"
            sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '-0.02em' }}
          >
            {dialogTitle}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              backgroundColor: 'rgba(255, 255, 255, 0.45)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {enableSupportSection ? (
          <>
            <Box
              sx={{
                px: 0,
                pt: 0,
                flexShrink: 0,
                borderBottom: '1px solid rgba(255, 255, 255, 0.45)',
                background: 'rgba(255, 255, 255, 0.22)',
              }}
            >
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="fullWidth"
                sx={{
                  px: 1.5,
                  minHeight: 44,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    minHeight: 44,
                    py: 0.75,
                    color: 'rgba(0, 0, 0, 0.5)',
                    transition: 'color 0.2s ease, background-color 0.2s ease',
                  },
                  '& .Mui-selected': {
                    color: 'primary.main',
                    backgroundColor: 'rgba(255, 255, 255, 0.35)',
                  },
                  '& .MuiTabs-indicator': {
                    height: 2,
                    borderRadius: '4px 4px 0 0',
                    background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                  },
                }}
              >
                <Tab icon={<PlayCircleOutlineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Tutorial" />
                <Tab icon={<SupportAgentIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Support" />
              </Tabs>
            </Box>

            <DialogContent sx={tabbedContentSx}>
              {tab === 0 && tutorialPanel}
              {tab === 1 && supportPanel}
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 1.5,
                pt: 1,
                flexShrink: 0,
                justifyContent: 'flex-end',
                borderTop: '1px solid rgba(255, 255, 255, 0.4)',
                background: 'rgba(255, 255, 255, 0.18)',
              }}
            >
              <Button
                onClick={onClose}
                variant="text"
                size="small"
                sx={{
                  color: 'text.secondary',
                  fontSize: '13px',
                  fontWeight: 500,
                  textTransform: 'none',
                  minWidth: 0,
                  px: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.45)',
                  },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent
              sx={{
                pt: 3,
                pb: 2,
                px: 3,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(240,248,255,0.4) 100%)',
              }}
            >
              {tutorialPanel}
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 2.5,
                pt: 0,
                borderTop: '1px solid rgba(255, 255, 255, 0.45)',
                background: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <Button
                onClick={onClose}
                variant="text"
                sx={{
                  color: 'text.secondary',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.45)',
                  },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {showMiniPlayer && (
        <MiniVideoPlayer videoUrl={videoUrl} title={title} onClose={handleCloseMiniPlayer} />
      )}
    </>
  );
};

export default HelpModal;
